import React, { useCallback } from "react";
import { Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import type { NavigateOptions } from "react-router-dom";
import { useNavigationConfirm } from "./hooks/useNavigationConfirm";
import CustomModal from "../Modal/CustomModal";
import CustomButton from "../Buttons/Button/CustomButton";

export interface CustomFormNavigationGuardProps {
  /** フォームが変更されているかどうか */
  hasFormChanged: boolean;
  /** 送信中かどうか（追加のブロック条件） */
  isSubmitting?: boolean;
  /** 確認モーダルのメッセージ（関数も可能） */
  message?: string | ((isProcessing: boolean) => string);
  /** 確認ボタンのラベル（関数も可能） */
  confirmLabel?: string | ((isProcessing: boolean) => string);
  /** キャンセルボタンのラベル */
  cancelLabel?: string;
  /** モーダルのタイトル（関数も可能） */
  title?: string | ((isProcessing: boolean) => string);
  /** 追加の条件（例：処理中、ファイルアップロード中など） */
  additionalBlockConditions?: boolean;
  /**
   * 子コンポーネントをレンダリングするためのrender prop
   *
   * 注意: CustomFormNavigationGuardは自動的にナビゲーションをインターセプトします。
   * カスタムナビゲーションが必要な場合は、children内でnavigateWithConfirmやnavigateWithoutConfirmを使用できます。
   */
  children?: () => React.ReactNode;
  /**
   * 指定時のみ使用。遷移ヘルパーを渡す render prop（既存の children?.() とは排他ではないが、通常はどちらか一方を使う）
   */
  childrenWithNavigation?: (
    navigateWithConfirm: (to: string, options?: NavigateOptions) => void,
    navigateWithoutConfirm: (to: string, options?: NavigateOptions) => void,
    confirmDelete: () => void,
  ) => React.ReactNode;
  /** 確認モーダルで「移動」をクリックした時の追加処理 */
  onConfirm?: () => void;
  /** タブ切り替え時の確認を無効にするかどうか */
  ignoreTabNavigation?: boolean;
  leaveConfirmModalType?: "deleteModal" | "otherModal";
  /** 離脱確認のキャンセルボタン category（既定: cancelModal） */
  leaveConfirmCancelButtonCategory?: "cancel" | "cancelModal";
  /** 離脱確認 CustomModal に渡す sx */
  leaveConfirmModalSx?: object;
  /**
   * true のとき navigateWithoutConfirm 実行前に onConfirm を呼ぶ（sales-brokerage 既存挙動に合わせる用。既定 false）
   */
  callOnConfirmWhenNavigateWithoutConfirm?: boolean;
  /** true のとき confirmDelete で組み込み削除確認モーダルを表示（既定 false） */
  enableBuiltInDeleteConfirmModal?: boolean;
  builtInDeleteConfirmTitle?: string;
  builtInDeleteConfirmBodyText?: string;
  /** 削除モーダル確定時。未指定時は onConfirm */
  onBuiltInDeleteConfirm?: () => void;
}

const CustomFormNavigationGuard: React.FC<CustomFormNavigationGuardProps> = ({
  hasFormChanged,
  isSubmitting = false,
  message = "入力中の情報が失われますが、よろしいでしょうか？",
  confirmLabel = "移動",
  cancelLabel = "戻る",
  title = "ページ移動の確認",
  additionalBlockConditions = false,
  children,
  childrenWithNavigation,
  onConfirm,
  ignoreTabNavigation = false,
  leaveConfirmModalType = "deleteModal",
  leaveConfirmCancelButtonCategory = "cancelModal",
  leaveConfirmModalSx = { padding: "0px !important" },
  callOnConfirmWhenNavigateWithoutConfirm = false,
  enableBuiltInDeleteConfirmModal = false,
  builtInDeleteConfirmTitle = "削除の確認",
  builtInDeleteConfirmBodyText =
    "ファイルと抽出データを削除しますか？抽出されたデータは失われます。",
  onBuiltInDeleteConfirm,
}) => {
  const navigate = useNavigate();
  const [temporarilyAllowNavigation, setTemporarilyAllowNavigation] =
    React.useState(false);
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);

  const shouldBlockNavigation =
    (hasFormChanged || isSubmitting || additionalBlockConditions) &&
    !temporarilyAllowNavigation;

  const isProcessing = isSubmitting || additionalBlockConditions;

  const resolvedMessage =
    typeof message === "function" ? message(isProcessing) : message;
  const resolvedTitle =
    typeof title === "function" ? title(isProcessing) : title;
  const resolvedConfirmLabel =
    typeof confirmLabel === "function"
      ? confirmLabel(isProcessing)
      : confirmLabel;

  const {
    showModal,
    confirmNavigation: originalConfirmNavigation,
    cancelNavigation,
    modalMessage,
    navigateWithConfirm,
  } = useNavigationConfirm({
    shouldBlock: shouldBlockNavigation,
    message: resolvedMessage,
    ignoreTabNavigation,
  });

  const confirmNavigation = useCallback(() => {
    setTemporarilyAllowNavigation(true);
    if (onConfirm) {
      onConfirm();
    }
    setTimeout(() => {
      originalConfirmNavigation();
      setTimeout(() => {
        setTemporarilyAllowNavigation(false);
      }, 1000);
    }, 50);
  }, [originalConfirmNavigation, onConfirm]);

  const navigateWithoutConfirm = useCallback(
    (to: string, options?: NavigateOptions) => {
      setTemporarilyAllowNavigation(true);
      if (callOnConfirmWhenNavigateWithoutConfirm) {
        onConfirm?.();
      }
      navigate(to, options);
      setTimeout(() => {
        setTemporarilyAllowNavigation(false);
      }, 500);
    },
    [navigate, onConfirm, callOnConfirmWhenNavigateWithoutConfirm],
  );

  const confirmDelete = useCallback(() => {
    if (enableBuiltInDeleteConfirmModal) {
      setShowDeleteModal(true);
    }
  }, [enableBuiltInDeleteConfirmModal]);

  const handleDeleteCancel = useCallback(() => {
    setShowDeleteModal(false);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    setShowDeleteModal(false);
    (onBuiltInDeleteConfirm ?? onConfirm)?.();
  }, [onConfirm, onBuiltInDeleteConfirm]);

  return (
    <>
      {childrenWithNavigation
        ? childrenWithNavigation(
            navigateWithConfirm,
            navigateWithoutConfirm,
            confirmDelete,
          )
        : children?.()}

      {/* ナビゲーション離脱確認モーダル。既定の modalType="deleteModal" は CustomModal で本文(bodyText)を表示するために指定 */}
      <CustomModal
        openModal={showModal}
        handleCloseModal={cancelNavigation}
        title={resolvedTitle}
        bodyText={modalMessage}
        modalType={leaveConfirmModalType}
        bodyTextAlign="left"
        sx={leaveConfirmModalSx}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: "10px",
          }}
        >
          <CustomButton
            label={cancelLabel}
            buttonCategory={leaveConfirmCancelButtonCategory}
            onClick={cancelNavigation}
            sx={{
              width: "50% !important",
            }}
          />
          <CustomButton
            label={resolvedConfirmLabel}
            buttonCategory="confirm"
            onClick={confirmNavigation}
            sx={{
              width: "50% !important",
            }}
          />
        </Box>
      </CustomModal>

      {enableBuiltInDeleteConfirmModal ? (
        <CustomModal
          openModal={showDeleteModal}
          handleCloseModal={handleDeleteCancel}
          title={builtInDeleteConfirmTitle}
          bodyText={builtInDeleteConfirmBodyText}
          modalType="otherModal"
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: "10px",
            }}
          >
            <CustomButton
              label="戻る"
              buttonCategory="cancelModal"
              onClick={handleDeleteCancel}
            />
            <CustomButton
              label="削除"
              buttonCategory="deleteModal"
              onClick={handleDeleteConfirm}
            />
          </Box>
        </CustomModal>
      ) : null}
    </>
  );
};

export default CustomFormNavigationGuard;
